/**
 * toolEnvelope.ts
 *
 * Shared type + parser for the JSON envelope every OneClaw OneThing tool emits
 * as TOOL_CALL_RESULT.content:
 *
 *   { ok: true,  kind: "priority_list", summary: "...", data: [...] }
 *   { ok: false, kind: "priority_list", summary: "...", error: "..." }
 *
 * `parseEnvelope` is the ONLY place we do JSON.parse — all FE card code calls
 * this and trusts the returned shape. It never throws.
 */

// ---------------------------------------------------------------------------
// Envelope type
// ---------------------------------------------------------------------------

export interface ToolEnvelope {
  /** Whether the tool call succeeded. */
  ok: boolean;
  /**
   * Stable routing key (e.g. "priority_list", "priority_detail").
   * Determines which card component renders the result.
   */
  kind: string;
  /** Human-readable summary — shown as card subtitle and LLM-facing text. */
  summary: string;
  /** Structured payload; present on success, absent (or undefined) on error. */
  data?: unknown;
  /** Error message; present when ok === false. */
  error?: string;
}

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/**
 * Parse the raw TOOL_CALL_RESULT content into a ToolEnvelope.
 *
 * Accepts:
 *  - A JSON string (normal case — TOOL_CALL_RESULT.content is a string)
 *  - An already-parsed object (defensive; some CopilotKit versions may unwrap)
 *
 * Returns null on ANY failure so callers can fall back to raw rendering.
 * Never throws.
 */
export function parseEnvelope(result: unknown): ToolEnvelope | null {
  try {
    // Unwrap string to object
    let obj: unknown = typeof result === "string" ? JSON.parse(result) : result;

    // The OneClaw AG-UI adapter serializes the WHOLE MCP tool-result object
    // ({ content: [{ type:"text", text:"<envelope JSON>" }] }) into
    // TOOL_CALL_RESULT.content — so our envelope arrives nested one level deep.
    // Unwrap content[].text (the first text part) and re-parse before validating.
    const inner = (obj as Record<string, unknown> | null)?.content;
    if (Array.isArray(inner)) {
      const textPart = inner.find(
        (p): p is { text: string } =>
          !!p && typeof (p as { text?: unknown }).text === "string",
      );
      if (textPart) {
        try {
          obj = JSON.parse(textPart.text);
        } catch {
          /* leave obj as-is; validation below will reject */
        }
      }
    }

    // Some backends double-encode the envelope (JSON.stringify applied twice),
    // so after unwrapping content[].text we may still be holding a JSON *string*
    // rather than the object. Parse one more level so validation sees the object.
    if (typeof obj === "string") {
      try {
        obj = JSON.parse(obj);
      } catch {
        /* leave obj as-is; validation below will reject */
      }
    }

    // Validate required fields
    if (
      obj === null ||
      typeof obj !== "object" ||
      typeof (obj as Record<string, unknown>).ok !== "boolean" ||
      typeof (obj as Record<string, unknown>).kind !== "string" ||
      typeof (obj as Record<string, unknown>).summary !== "string"
    ) {
      return null;
    }

    const raw = obj as Record<string, unknown>;

    return {
      ok: raw.ok as boolean,
      kind: raw.kind as string,
      summary: raw.summary as string,
      // optional fields — only attach if present to keep shape minimal
      ...(raw.data !== undefined ? { data: raw.data } : {}),
      ...(typeof raw.error === "string" ? { error: raw.error } : {}),
    };
  } catch {
    return null;
  }
}
